// src/pages/mockCasePage/MockCaseTimeline/index.tsx

import React, { useContext } from "react";
import styles from "./index.module.css";
import { InterviewContext } from "../../../contexts/InterviewContext";
import { Button } from "../../../components/ui/Button";
import { Info } from "lucide-react";
import FallbackInformation from "../MockCaseMain/FallbackInformation";

interface MockCaseTimelineProps {
  currentStep?: number;
  totalSteps?: number;
  questionTypes?: string[];
}

function getPhaseData(questionType: string) {
  switch (questionType) {
    case "prompt":
      return {
        phase: "Prompt & Opening",
        question: "What's your understanding of the problem?",
        hints: `1. Recap the prompt and confirm understanding\n2. Ask clarifying questions to help structure your approach.`,
      };
    case "structure":
      return {
        phase: "Structure",
        question: "How would you structure your approach to this case?",
        hints:
          "1. Ask for a few minutes to structure your thoughts\n2. Provide a clear and logical structure by first going thorough the key areas\n3. Then explain how you would tackle each area",
      };
    case "math":
      return {
        phase: "Math / Analysis",
        question: "Walk me through your market sizing or cost analysis.",
        hints:
          "1. Describe the visuals and data you need\n2. Ask for more data if needed\n3. Explain your calculations and assumptions before diving in",
      };
    case "recommendation":
      return {
        phase: "Recommendation",
        question: "What are your final recommendations and rationale?",
        hints:
          "Summarize your findings, provide a concise conclusion, and outline risks and next steps",
      };
    case "brainstorm":
      return {
        phase: "Brainstorm",
        question: "Generate multiple ideas or solutions for the client.",
        hints:
          "Come up with as many ideas as possible with a structure. Go wide first, then refine. Show creativity while staying grounded in the case context",
      };
    default:
      return {
        phase: "Unknown Phase",
        question: "",
        hints: "",
      };
  }
}

const HintDisplay: React.FC<{ hints: string; isBlurred: boolean }> = ({
  hints,
  isBlurred,
}) => {
  const displayHints = isBlurred ? "this is blurred information" : hints;
  const hintLines = displayHints.split("\n");
  return (
    <div
      className={styles.hintsContainer}
      style={{
        filter: isBlurred ? `blur(3px)` : "none",
      }}
    >
      {hintLines.map((line, index) => (
        <p key={index} className={styles.hintLine}>
          {line}
        </p>
      ))}
    </div>
  );
};

const TimelineSegment: React.FC<{
  phase: string;
  hints: string;
  isCompleted: boolean;
  isCurrentStep: boolean;
  hasFallbackInfo: boolean;
  stepNumber: number;
}> = ({
  phase,
  hints,
  isCompleted,
  isCurrentStep,
  hasFallbackInfo,
  stepNumber,
}) => {
  return (
    <div
      className={`${styles.timelineSegment} ${
        isCompleted ? styles.completed : ""
      }`}
    >
      <div className={styles.phaseHeader}>
        <span>{phase}</span>
        {hasFallbackInfo && <FallbackInformation className="ml-2" />}
      </div>
      <HintDisplay hints={hints} isBlurred={!isCurrentStep} />
    </div>
  );
};

const MockCaseTimeline: React.FC<MockCaseTimelineProps> = ({
  currentStep = 0,
  totalSteps = 0,
  questionTypes = [],
}) => {
  const { fallbackInformation, fallbackInformationQuestionNumber } =
    useContext(InterviewContext)!;

  if (totalSteps === 0) {
    return (
      <div className={styles.timelineContainer}>
        <p className={styles.noStepsText}>
          No steps to display. (totalSteps = 0)
        </p>
      </div>
    );
  }

  const steps = Array.from({ length: totalSteps }, (_, i) => i + 1);

  return (
    <div className={styles.timelineContainer}>
      {steps.map((step) => {
        const currentType = questionTypes[step - 1] || "prompt";
        const { phase, hints } = getPhaseData(currentType);
        const isCompleted = step <= currentStep;

        // Show fallback info if:
        // 1. It's the current step and fallback information is available, OR
        // 2. It's step 1 (prompt phase) and fallback information is available for question 0
        const hasFallbackInfo =
          (currentStep === step &&
            fallbackInformation &&
            fallbackInformation.length > 0) ||
          (step === 1 &&
            fallbackInformationQuestionNumber === 0 &&
            fallbackInformation &&
            fallbackInformation.length > 0);

        return (
          <TimelineSegment
            key={step}
            phase={phase}
            hints={hints}
            isCompleted={isCompleted}
            isCurrentStep={currentStep === step}
            hasFallbackInfo={hasFallbackInfo}
            stepNumber={step}
          />
        );
      })}
    </div>
  );
};

export default MockCaseTimeline;
